import { hourAxis } from '@/components/charts/generic/hourAxis';
import type { CageDepthSeries } from '@/domain/depthSeries';
import type { CompareCage, CompareMetric } from '@/domain/locationCompare';
import { formatUniversalUnitByLocale } from '@/utils/localeFormatters';

export { clampHourlyRange, MAX_HOURLY_DEPTH_DAYS } from '@/domain/depthSeries/hourlyRange';

/**
 * Compare pens, plus any pen the hourly series has that the daily response dropped.
 *
 * A midday move gives that day's daily row to one pen. The other pen can still have hourly
 * readings and no other metric, so compare leaves it out of the legend.
 */
export function cagesForHourlyDepth(
    compareCages: CompareCage[],
    series: CageDepthSeries[]
): CompareCage[] {
    const known = new Set(compareCages.map((cage) => cage.id));
    const extra = series
        .filter((cage) => !known.has(cage.cageId))
        .map((cage) => ({
            id: cage.cageId,
            name: cage.name !== '' ? cage.name : String(cage.cageId),
        }))
        .sort((a, b) => a.id - b.id);
    return extra.length === 0 ? compareCages : [...compareCages, ...extra];
}

/**
 * One compare series per pen, on an hour axis with the missing hours present as gaps.
 */
export function hourlyDepthMetric(cages: CompareCage[], series: CageDepthSeries[]): CompareMetric {
    const byCage = new Map(
        series.map((cage) => [
            cage.cageId,
            new Map(cage.points.map((point) => [point.time, point.value])),
        ])
    );
    const hours = hourAxis(
        series.flatMap((cage) => cage.points),
        (point) => point.time
    );

    return {
        dataPoints: hours.map((hour) => ({
            day: hour,
            values: Object.fromEntries(
                cages.map((cage) => [cage.id, byCage.get(cage.id)?.get(hour) ?? null])
            ),
        })),
        formatValue: (value, locale) => formatUniversalUnitByLocale(value, 'm', locale, 1),
        beginAtZero: false,
        reverse: true,
    };
}
